import { Anthropic } from '@anthropic-ai/sdk';
import { tools, executeFunction } from './tools';
import { buildSystemPrompt } from './systemPrompt';

// Local storage key for Anthropic API key
export const ANTHROPIC_API_KEY_STORAGE_KEY = 'paybyrd_anthropic_api_key';
export const ANTHROPIC_CHAT_HISTORY_KEY = 'paybyrd_anthropic_chat_history';

/**
 * Get Anthropic client instance
 * @returns {Anthropic|null} Anthropic client instance or null if no API key is available
 */
export const getAnthropicClient = () => {
  const apiKey = localStorage.getItem(ANTHROPIC_API_KEY_STORAGE_KEY);
  if (!apiKey) {
    return null;
  }

  return new Anthropic({
    apiKey,
    dangerouslyAllowBrowser: true
  });
};

/**
 * Get tools formatted for Claude
 * @returns {Array} Tools in Claude format
 */
export const getClaudeTools = () => {
  return tools.map(tool => ({
    name: tool.name,
    description: tool.description,
    input_schema: tool.parameters
  }));
};

/**
 * Check if Anthropic API key is set
 * @returns {boolean} True if API key is set
 */
export const hasAnthropicApiKey = () => {
  return !!localStorage.getItem(ANTHROPIC_API_KEY_STORAGE_KEY);
};

/**
 * Set Anthropic API key
 * @param {string} apiKey - Anthropic API key
 */
export const setAnthropicApiKey = (apiKey) => {
  localStorage.setItem(ANTHROPIC_API_KEY_STORAGE_KEY, apiKey);
};

/**
 * Clear Anthropic API key
 */
export const clearAnthropicApiKey = () => {
  localStorage.removeItem(ANTHROPIC_API_KEY_STORAGE_KEY);
};

/**
 * Get saved chat history from local storage for a specific store
 * @param {string} storeId - The store ID or 'global' if no store is selected
 * @returns {Array} Chat history messages
 */
export const getChatHistory = (storeId = 'global') => {
  try {
    const key = `${ANTHROPIC_CHAT_HISTORY_KEY}_${storeId}`;
    const storedHistory = localStorage.getItem(key);
    if (storedHistory) {
      return JSON.parse(storedHistory);
    }
    
    // Fallback to global history if no store-specific history exists
    if (storeId !== 'global') {
      const globalHistory = localStorage.getItem(ANTHROPIC_CHAT_HISTORY_KEY);
      if (globalHistory) {
        return JSON.parse(globalHistory);
      }
    }
  } catch (error) {
    console.error('Error retrieving chat history:', error);
  }

  return [];
};

/**
 * Save chat history to local storage for a specific store
 * @param {Array} history - Chat history to save
 * @param {string} storeId - The store ID or 'global' if no store is selected
 */
export const saveChatHistory = (history, storeId = 'global') => {
  try {
    // Limit history to last 20 messages to prevent localStorage size issues
    const limitedHistory = history.slice(-20);
    const key = `${ANTHROPIC_CHAT_HISTORY_KEY}_${storeId}`;
    localStorage.setItem(key, JSON.stringify(limitedHistory));
  } catch (error) {
    console.error('Error saving chat history:', error);
  }
};

/**
 * Clear chat history from local storage for a specific store
 * @param {string} storeId - The store ID or 'global' if no store is selected
 */
export const clearChatHistory = (storeId = 'global') => {
  const key = `${ANTHROPIC_CHAT_HISTORY_KEY}_${storeId}`;
  localStorage.removeItem(key);
};

/**
 * Handle Claude tool uses by executing the corresponding functions
 * @param {Object} toolUse - Claude tool use object 
 * @param {string} accessToken - Auth token for API authorization
 * @param {Object} selectedStore - Selected store for context
 * @returns {Promise<Object>} Tool result in Claude format
 */
const handleToolUse = async (toolUse, accessToken, selectedStore) => {
  try {
    const functionName = toolUse.name;
    const args = toolUse.input;
    
    const response = await executeFunction(
      functionName,
      args,
      accessToken,
      selectedStore
    );
    
    return {
      type: 'tool_result',
      tool_use_id: toolUse.id,
      content: response
    };
  } catch (error) {
    console.error(`Error handling tool use ${toolUse.name}:`, error);
    return {
      type: 'tool_result',
      tool_use_id: toolUse.id,
      content: JSON.stringify(`Error executing ${toolUse.name}: ${error.message || 'Unknown error'}`)
    };
  }
};

/**
 * Send message to Anthropic Claude with conversation history
 * @param {string} message - User message
 * @param {Array} previousMessages - Previous messages in the conversation
 * @param {string} accessToken - Auth token for API authorization
 * @param {Object} selectedStore - Optional selected store for context
 * @returns {Promise<string>} AI response
 */
export const sendMessageToAnthropic = async (message, previousMessages = [], accessToken = null, selectedStore = null) => {
  const anthropic = getAnthropicClient();

  if (!anthropic) {
    throw new Error('Anthropic API key not set');
  }

  try {
    // Extract store info from message if it's in the format we're sending
    let userMessage = message;
    let extractedStore = selectedStore;

    // Check if message includes store context
    if (message.startsWith('Current selected store:')) {
      // Try to extract store ID from message format
      const storeMatch = message.match(/Current selected store: (.*?) \(ID: (.*?)\)/);
      if (storeMatch && storeMatch.length >= 3) {
        extractedStore = {
          label: storeMatch[1],
          value: storeMatch[2]
        };
        // Remove the store context from the message to avoid confusion
        const parts = message.split('\n\n');
        if (parts.length > 1) {
          userMessage = parts.slice(1).join('\n\n');
        }
      }
    }

    // Format previous messages for the API
    const formattedHistory = previousMessages
      .filter(msg => msg.text && msg.text.trim() !== '')
      .map(msg => ({
        role: msg.isUser ? 'user' : 'assistant',
        content: msg.text
      }));

    // Build system prompt with store context, current date and timezone
    const systemPrompt = buildSystemPrompt(extractedStore);

    // Create messages array for Claude API request
    const messages = [
      ...formattedHistory,
      { role: 'user', content: userMessage }
    ];
    
    // Get the selected model or use default
    const modelName = localStorage.getItem('paybyrd_anthropic_model') || 'claude-4-sonnet-20250514';
    
    // Use Claude API with tools
    const response = await anthropic.messages.create({
      model: modelName,
      max_tokens: 1000,
      temperature: 0.7,
      system: systemPrompt,
      messages,
      tools: getClaudeTools(),
    });
    
    // Check if response contains any tool use
    const hasToolUse = response.content.some(content => content.type === "tool_use");
    
    // If there's a tool use, we'll skip the initial text messages and only return the follow-up response
    if (hasToolUse) {
      // Process all tool uses in sequence
      let updatedMessages = [...messages];
      
      for (const content of response.content) {
        if (content.type === "tool_use") {
          try {
            // Execute tool call
            const toolResult = await handleToolUse(content, accessToken, extractedStore);
            
            // Add tool use to message history
            updatedMessages.push({
              role: 'assistant',
              content: [{ 
                type: 'tool_use', 
                id: content.id, 
                name: content.name, 
                input: content.input 
              }]
            });
            
            // Add user message with tool results
            updatedMessages.push({
              role: 'user',
              content: [toolResult]
            });
          } catch (error) {
            console.error(`Error handling tool ${content.name}:`, error);
            return `I was unable to complete the operation using ${content.name}. There was an error: ${error.message}`;
          }
        }
      }
      
      // Get final response from Claude with all tool results using the same model
      const followUpResponse = await anthropic.messages.create({
        model: modelName,
        max_tokens: 1000,
        system: systemPrompt,
        messages: updatedMessages,
      });
      
      // Return only the follow-up response text
      const followUpText = followUpResponse.content
        .filter(c => c.type === 'text')
        .map(c => c.text)
        .join('\n');
        
      return followUpText;
    } else {
      // If no tool use, return the original text response
      return response.content
        .filter(c => c.type === 'text')
        .map(c => c.text)
        .join('\n');
    }
  } catch (error) {
    console.error('Error calling Anthropic API:', error);
    throw error;
  }
};